import { useState, useEffect } from 'react';
import api from '../services/api';
import { toast } from 'sonner';
import * as XLSX from 'xlsx'; // Excel kütüphanesi eklendi

// UI Bileşenleri
import { Card, CardContent } from './ui/card.tsx';
import { Badge } from './ui/badge.tsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs.tsx';
import { Shield, Users, Calendar, BarChart, Mail } from 'lucide-react';

// Admin Tab Components
import { AnnouncementsTab } from './admin/AnnouncementsTab.tsx';
import { EventsTab } from './admin/EventsTab.tsx';
import { MembersTab } from './admin/MembersTab.tsx';
import { BoardTab } from './admin/BoardTab.tsx';
import { GalleryTab } from './admin/GalleryTab.tsx';
import { MessagesTab } from './admin/MessagesTab.tsx';

// Modal Components
import { AttendeeModal } from './admin/AttendeeModal.tsx';
import { MemberEditModal } from './admin/MemberEditModal.tsx';

// --- TİP TANIMLARI ---
interface AdminAnnouncement { 
    id: number; 
    Title: string; 
    Content: string; 
    Description: string; 
    Category: string; 
    PublishDate: string; 
    Image?: string | null;
}

interface AdminEvent { 
    id: number; 
    Title: string; 
    Description: string; 
    StartDate: string; 
    EndDate: string; 
    Location: string; 
    attendees_count?: number; 
    Image?: string | null; 
}

interface AdminMember { 
    id: number; 
    Email: string; 
    FirstName: string; 
    LastName: string; 
    GraduationYear: number; 
    is_staff: boolean; 
    AssociationRole?: string; 
} 

interface AdminGalleryFolder {
    id: number;
    name: string;
    visibility: 'public' | 'registered';
    created_at: string;
}

interface AdminContactMessage { 
    id: number; 
    name: string; 
    email: string; 
    subject: string; 
    message: string; 
    created_at: string; 
}

interface Attendee {
    id: number;
    user_name: string;
    user_surname: string;
    user_email: string;
    GuestCount: number;
}

export function AdminPanelPage() {
  // --- STATE'LER ---
  const [loading, setLoading] = useState(true);
  
  // Veri State'leri
  const [announcements, setAnnouncements] = useState<AdminAnnouncement[]>([]);
  const [events, setEvents] = useState<AdminEvent[]>([]);
  const [members, setMembers] = useState<AdminMember[]>([]);
  const [galleryFolders, setGalleryFolders] = useState<AdminGalleryFolder[]>([]); 
  const [messages, setMessages] = useState<AdminContactMessage[]>([]);
  
  // İstatistikler
  const [stats, setStats] = useState({ totalMembers: 0, activeEvents: 0, totalAnnouncements: 0, totalMessages: 0 });

  // Kontroller
  const [showAddForm, setShowAddForm] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Ortak Düzenleme State'leri
  const [editingId, setEditingId] = useState<number | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // --- FORM DATALARI ---
  const [newAnnouncement, setNewAnnouncement] = useState({ 
      title: '', description: '', content: '', category: 'GENEL', date: '' 
  });

  const [newEvent, setNewEvent] = useState({ 
      title: '', description: '', startDate: '', endDate: '', location: '' 
  });

  // --- GALERİ FORMLARI ---
  const [newAlbum, setNewAlbum] = useState({ 
      title: '', visibility: 'registered', category: 'etkinlikler'
  });
  
  const [newImage, setNewImage] = useState({
      albumId: '', title: ''
  });
  const [selectedGalleryFiles, setSelectedGalleryFiles] = useState<FileList | null>(null);
  
  // Katılımcı Listesi Modal State'leri
  const [attendeeList, setAttendeeList] = useState<Attendee[]>([]);
  const [showAttendeeModal, setShowAttendeeModal] = useState(false);
  const [selectedEventName, setSelectedEventName] = useState('');

  // --- ÜYE DÜZENLEME MODALI STATE'LERİ ---
  const [selectedMember, setSelectedMember] = useState<AdminMember | null>(null);
  const [memberRoleInput, setMemberRoleInput] = useState(''); 
  const [memberIsStaff, setMemberIsStaff] = useState(false); 


  // --- 1. VERİ ÇEKME ---
  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [annRes, evtRes, memRes, folderRes, msgRes] = await Promise.all([
        api.get('/announcements/'),
        api.get('/events/'),
        api.get('/users/'),
        api.get('/gallery/folders/'),
        api.get('/contact/list/'),
      ]);

      setAnnouncements(annRes.data);
      setEvents(evtRes.data);
      setMembers(memRes.data); 
      setGalleryFolders(folderRes.data);
      setMessages(msgRes.data);

      setStats({
        totalMembers: memRes.data.length,
        activeEvents: evtRes.data.filter((e: any) => new Date(e.StartDate) >= new Date()).length,
        totalAnnouncements: annRes.data.length,
        totalMessages: msgRes.data.length,
      });

    } catch (error) {
      console.error("Veri çekme hatası:", error);
      toast.error("Veriler yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // Yardımcı Fonksiyon: ISO Tarihini Input Formatına Çevir
  const formatForInput = (isoString: string) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const localIso = new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString(); 
    return localIso.slice(0, 16); 
  };

  // Duyuru/Etkinlik için tek dosya seçimi
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
        setSelectedFile(e.target.files[0]);
    }
  };

  // GALERİ İÇİN ÇOKLU DOSYA SEÇİMİ
  const handleGalleryFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
        setSelectedGalleryFiles(e.target.files);
    }
  };

  // --- 2. DUYURU İŞLEMLERİ ---
  const resetAnnouncementForm = () => {
      setShowAddForm(null);
      setEditingId(null);
      setNewAnnouncement({ title: '', description: '', content: '', category: 'GENEL', date: '' });
      setSelectedFile(null);
  };

  const handleSaveAnnouncement = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('Title', newAnnouncement.title);
      formData.append('Content', newAnnouncement.content);
      formData.append('Description', newAnnouncement.description || ''); 
      formData.append('Category', newAnnouncement.category);
      
      const publishDate = newAnnouncement.date ? new Date(newAnnouncement.date).toISOString() : new Date().toISOString();
      formData.append('PublishDate', publishDate);

      if (selectedFile) formData.append('Image', selectedFile);

      const config = { headers: { 'Content-Type': 'multipart/form-data' } };

      if (editingId) {
        await api.patch(`/announcements/${editingId}/`, formData, config);
        toast.success('Duyuru güncellendi!');
      } else {
        await api.post('/announcements/', formData, config);
        toast.success('Duyuru oluşturuldu!');
      }
      resetAnnouncementForm();
      await fetchAllData(); 
    } catch (error) { toast.error('İşlem başarısız.'); } finally { setLoading(false); }
  };

  const handleEditAnnouncement = (ann: AdminAnnouncement) => {
      setNewAnnouncement({
          title: ann.Title,
          description: ann.Description || '',
          content: ann.Content,
          category: ann.Category,
          date: ann.PublishDate.split('T')[0]
      });
      setEditingId(ann.id);
      setSelectedFile(null);
      setShowAddForm('announcement');
      window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteAnnouncement = async (id: number) => {
    if(!window.confirm("Silmek istediğine emin misin?")) return;
    try { await api.delete(`/announcements/${id}/`); toast.success('Silindi.'); await fetchAllData(); } 
    catch { toast.error('Hata.'); }
  };

  // --- 3. ETKİNLİK İŞLEMLERİ ---
  const resetEventForm = () => {
      setShowAddForm(null);
      setEditingId(null);
      setNewEvent({ title: '', description: '', startDate: '', endDate: '', location: '' });
      setSelectedFile(null);
  };

  const handleSaveEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('Title', newEvent.title);
      formData.append('Description', newEvent.description);
      formData.append('Location', newEvent.location);
      formData.append('StartDate', new Date(newEvent.startDate).toISOString());
      if(newEvent.endDate) formData.append('EndDate', new Date(newEvent.endDate).toISOString());
      
      if (selectedFile) formData.append('Image', selectedFile);

      const config = { headers: { 'Content-Type': 'multipart/form-data' } };

      if (editingId) {
        await api.patch(`/events/${editingId}/`, formData, config);
        toast.success('Etkinlik güncellendi!');
      } else {
        await api.post('/events/', formData, config);
        toast.success('Etkinlik oluşturuldu!');
      }
      resetEventForm();
      await fetchAllData();
    } catch (error) { toast.error('İşlem başarısız.'); } finally { setLoading(false); }
  };

  const handleEditEvent = (evt: AdminEvent) => {
      setNewEvent({
          title: evt.Title,
          description: evt.Description || '',
          location: evt.Location || '',
          startDate: formatForInput(evt.StartDate),
          endDate: evt.EndDate ? formatForInput(evt.EndDate) : ''
      });
      setEditingId(evt.id);
      setSelectedFile(null);
      setShowAddForm('event');
      window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteEvent = async (id: number) => {
    if(!window.confirm("Silinsin mi?")) return;
    try { await api.delete(`/events/${id}/`); toast.success('Silindi.'); await fetchAllData(); } 
    catch { toast.error('Hata.'); }
  };

  const handleViewAttendees = async (eventId: number, eventTitle: string) => {
      try {
          const res = await api.get(`/events/${eventId}/attendees/`);
          setAttendeeList(res.data);
          setSelectedEventName(eventTitle);
          setShowAttendeeModal(true);
      } catch (error) {
          toast.error("Katılımcı listesi alınamadı.");
      }
  };

  // --- EXCEL İNDİRME FONKSİYONU ---
  // --- EXCEL İNDİRME FONKSİYONU ---
  const handleDownloadExcel = () => {
    if (attendeeList.length === 0) {
      toast.error("İndirilecek katılımcı bulunamadı.");
      return;
    }

    // 1. Etkinliğe katılacak genel toplam kişi sayısını hesapla (Kayıtlılar + Tüm Misafirler)
    const grandTotal = attendeeList.reduce((total, item) => total + 1 + item.GuestCount, 0);

    // 2. Excel verilerini hazırla
    const excelData: any[] = attendeeList.map((attendee, index) => ({
      "Sıra": index + 1,
      "Ad": attendee.user_name,
      "Soyad": attendee.user_surname,
      "E-posta": attendee.user_email,
      "Misafir Sayısı": attendee.GuestCount
    }));

    // 3. Listenin en sonuna boş bir satır ekle (görsel olarak ayırmak için)
    excelData.push({
      "Sıra": "",
      "Ad": "",
      "Soyad": "",
      "E-posta": "",
      "Misafir Sayısı": ""
    });

    // 4. Genel Toplam satırını ekle
    excelData.push({
      "Sıra": "",
      "Ad": "",
      "Soyad": "",
      "E-posta": "ETKİNLİK GENEL TOPLAMI:",
      "Misafir Sayısı": grandTotal
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Katılımcılar");

    XLSX.writeFile(workbook, `${selectedEventName}_Katilimcilar.xlsx`);
  };

  // --- 4. ÜYE YÖNETİMİ VE YETKİLENDİRME ---
  const handleDeleteMember = async (memberId: number) => {
    if (!window.confirm("Bu üyeyi silmek istediğine emin misin?")) return;

    try {
      await api.delete(`/users/${memberId}/`);
      toast.success("Üye silindi.");
      await fetchAllData(); 
    } catch (error) {
      console.error(error);
      toast.error("Üye silinemedi.");
    }
  };

  const openMemberModal = (member: AdminMember) => {
    setSelectedMember(member);
    setMemberRoleInput(member.AssociationRole || ''); 
    setMemberIsStaff(member.is_staff); 
  };

  const handleSaveMemberChanges = async () => {
    if (!selectedMember) return;
    try {
        await api.patch(`/users/${selectedMember.id}/`, {
            AssociationRole: memberRoleInput, 
            is_staff: memberIsStaff 
        });
        toast.success('Kullanıcı yetkileri ve rolü güncellendi!');
        setSelectedMember(null); 
        await fetchAllData(); 
    } catch (error) {
        toast.error("Güncelleme başarısız oldu.");
    }
  };

  const handleRemoveFromBoard = async (memberId: number) => {
    if (!window.confirm("Bu kişiyi yönetim kurulundan çıkarmak istediğinize emin misiniz?")) return;
    
    try {
        await api.patch(`/users/${memberId}/`, {
            AssociationRole: '' 
        });
        toast.success("Kişi yönetim kurulundan çıkarıldı.");
        await fetchAllData();
    } catch (error) {
        console.error(error);
        toast.error("İşlem başarısız.");
    }
  };

  // --- 5. GALERİ İŞLEMLERİ ---
  const handleCreateAlbum = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
        await api.post('/gallery/folders/create/', {
            name: newAlbum.title,
            visibility: newAlbum.visibility || "registered",
            category: newAlbum.category
        });
        toast.success("Albüm oluşturuldu.");
        setNewAlbum({ title: '', visibility: 'registered', category: 'etkinlikler' });
        await fetchAllData(); 
    } catch (error) {
        console.error(error);
        toast.error("Albüm oluşturulamadı.");
    } finally {
        setLoading(false);
    }
  };

  const handleDeleteAlbum = async (id: number) => {
      if(!window.confirm("Bu albümü ve içindeki tüm referansları silmek istediğinize emin misiniz?")) return;
      try {
          await api.delete(`/gallery/folders/${id}/`); 
          toast.success("Albüm silindi.");
          await fetchAllData();
      } catch (error) {
          console.error(error);
          toast.error("Albüm silinemedi.");
      }
  };

  const handleUploadImage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedGalleryFiles || selectedGalleryFiles.length === 0) { 
        toast.error("Lütfen en az bir resim dosyası seçin."); 
        return; 
    }
    
    setLoading(true);
    try {
        const uploadPromises = Array.from(selectedGalleryFiles).map((file) => {
            const formData = new FormData();
            if (newImage.albumId) formData.append('folder', newImage.albumId);
            if (newImage.title) formData.append('caption', newImage.title);
            formData.append('image', file); 

            return api.post('/gallery/images/upload/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        });

        await Promise.all(uploadPromises);

        toast.success(`${selectedGalleryFiles.length} adet resim başarıyla yüklendi.`);
        
        setNewImage({ albumId: '', title: '' });
        setSelectedGalleryFiles(null);
        
        const fileInput = document.getElementById('gallery-file-input') as HTMLInputElement;
        if(fileInput) fileInput.value = '';

    } catch (error) {
        console.error(error);
        toast.error("Yükleme sırasında hata oluştu. Bazı dosyalar yüklenememiş olabilir.");
    } finally {
        setLoading(false);
    }
  };

  // --- 6. DİĞER İŞLEMLER ---
  const handleDeleteMessage = async (id: number) => {
      if(!window.confirm("Silinsin mi?")) return;
      try { await api.delete(`/contact/list/${id}/`); toast.success('Silindi.'); await fetchAllData(); } catch { toast.error('Hata.'); }
  };

  // --- RENDER ---
  if (loading && announcements.length === 0) return <div className="text-center py-10">Yükleniyor...</div>;

  return (
    <div className="space-y-6 pb-12 relative">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Shield className="h-8 w-8 text-red-600" /> Yönetim Paneli
          </h1>
        </div>
        <Badge className="bg-red-600">Admin</Badge>
      </div>

      {/* İstatistikler */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6 flex justify-between">
            <div>
              <p className="text-sm">Toplam Üye</p>
              <p className="text-2xl font-bold">{stats.totalMembers}</p>
            </div>
            <Users className="opacity-50 text-orange-600"/>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 flex justify-between">
            <div>
              <p className="text-sm">Etkinlikler</p>
              <p className="text-2xl font-bold">{stats.activeEvents}</p>
            </div>
            <Calendar className="opacity-50 text-blue-600"/>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 flex justify-between">
            <div>
              <p className="text-sm">Duyurular</p>
              <p className="text-2xl font-bold">{stats.totalAnnouncements}</p>
            </div>
            <BarChart className="opacity-50 text-green-600"/>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 flex justify-between">
            <div>
              <p className="text-sm">Mesajlar</p>
              <p className="text-2xl font-bold">{stats.totalMessages}</p>
            </div>
            <Mail className="opacity-50 text-purple-600"/>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="announcements" className="w-full">
        <TabsList className="grid w-full grid-cols-6 h-auto p-1">
          <TabsTrigger value="announcements">Duyurular</TabsTrigger>
          <TabsTrigger value="events">Etkinlikler</TabsTrigger>
          <TabsTrigger value="members">Üyeler</TabsTrigger>
          <TabsTrigger value="board">Kurul</TabsTrigger>
          <TabsTrigger value="gallery">Galeri</TabsTrigger>
          <TabsTrigger value="messages" className="text-purple-700 data-[state=active]:bg-purple-100">
            Mesajlar
          </TabsTrigger>
        </TabsList>

        {/* 1. DUYURULAR */}
        <TabsContent value="announcements" className="space-y-4 mt-6">
          <AnnouncementsTab
            announcements={announcements}
            showAddForm={showAddForm}
            setShowAddForm={setShowAddForm}
            newAnnouncement={newAnnouncement}
            setNewAnnouncement={setNewAnnouncement}
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            handleFileChange={handleFileChange}
            handleSaveAnnouncement={handleSaveAnnouncement}
            resetAnnouncementForm={resetAnnouncementForm}
            handleEditAnnouncement={handleEditAnnouncement}
            handleDeleteAnnouncement={handleDeleteAnnouncement}
            editingId={editingId}
          />
        </TabsContent>

        {/* 2. ETKİNLİKLER */}
        <TabsContent value="events" className="space-y-4 mt-6">
          <EventsTab
            events={events}
            showAddForm={showAddForm}
            setShowAddForm={setShowAddForm}
            newEvent={newEvent}
            setNewEvent={setNewEvent}
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            handleFileChange={handleFileChange}
            handleSaveEvent={handleSaveEvent}
            resetEventForm={resetEventForm}
            handleEditEvent={handleEditEvent}
            handleDeleteEvent={handleDeleteEvent}
            handleViewAttendees={handleViewAttendees}
            editingId={editingId}
          />
        </TabsContent>

        {/* 3. ÜYELER */}
        <TabsContent value="members" className="mt-6">
          <MembersTab
            members={members}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            openMemberModal={openMemberModal}
            handleDeleteMember={handleDeleteMember}
          />
        </TabsContent>
        
        {/* 4. KURUL */}
        <TabsContent value="board" className="mt-6">
          <BoardTab
            members={members}
            handleRemoveFromBoard={handleRemoveFromBoard}
          />
        </TabsContent>

        {/* 5. GALERİ */}
        <TabsContent value="gallery" className="mt-6 space-y-6">
          <GalleryTab
            galleryFolders={galleryFolders}
            newAlbum={newAlbum}
            setNewAlbum={setNewAlbum}
            newImage={newImage}
            setNewImage={setNewImage}
            selectedGalleryFiles={selectedGalleryFiles}
            handleGalleryFileChange={handleGalleryFileChange}
            handleCreateAlbum={handleCreateAlbum}
            handleDeleteAlbum={handleDeleteAlbum}
            handleUploadImage={handleUploadImage}
            loading={loading}
          />
        </TabsContent>

        {/* 6. MESAJLAR */}
        <TabsContent value="messages" className="mt-6">
          <MessagesTab
            messages={messages}
            handleDeleteMessage={handleDeleteMessage}
          />
        </TabsContent>
      </Tabs>

      {/* MODALS */}
      <AttendeeModal
        showAttendeeModal={showAttendeeModal}
        setShowAttendeeModal={setShowAttendeeModal}
        selectedEventName={selectedEventName}
        attendeeList={attendeeList}
        handleDownloadExcel={handleDownloadExcel}
      />

      <MemberEditModal
        selectedMember={selectedMember}
        setSelectedMember={setSelectedMember}
        memberRoleInput={memberRoleInput}
        setMemberRoleInput={setMemberRoleInput}
        memberIsStaff={memberIsStaff}
        setMemberIsStaff={setMemberIsStaff}
        handleSaveMemberChanges={handleSaveMemberChanges}
      />
    </div>
  );
}